import {
  VuexModule,
  Module,
  MutationAction,
  getModule,
  Mutation,
  Action,
} from "vuex-module-decorators";
import store from "@/store";
import router from "@/router";

import { ContractsService } from "@/services/api/contracts.service";
import { Contract } from "~/contract.type";
import {
  ContractInfo,
  PayDetail,
  PayDetailDTO,
  Period,
  QiwiLink,
  QiwiPay,
  PayformPay,
  SmartBillingPay,
  Questionnaire,
  SendAnswersDTO,
  KaspiPay,
  KaspiLink,
} from "../../lib/contractinfo.type";
import { ContractStatus } from "../../lib/ContractStatus.type";
import { Limit } from "../../lib/limit.type";

import { DialogsModule } from "@/store/dialogs.module";
import { ReportsModule } from "@/store/reports.module";
import { AuthModule } from "./auth.module";
import { AuthService } from "@/services/auth.service";
@Module({
  dynamic: true,
  store,
  name: "contracts",
  preserveState: localStorage.getItem("vuex") !== null,
})
export class ContractsVuexModule extends VuexModule {
  showPayDialog: boolean = false;
  showRatingDialog: boolean = false;
  BINForRating: string = "";
  EmailForRating: string = "";
  contracts: Contract[] = [];
  selectedContract: number | null = null;
  contractInfo: ContractInfo = new ContractInfo();
  infoIsLoading: boolean = true;
  contractLimits: Limit[] = [];
  contractStatus: ContractStatus | null = null;
  showDebtNotice: boolean = false;
  curPeriod: Period = new Period();
  payDetail: PayDetail = new PayDetail();
  paySum: number = null as any;
  qiwiLink: QiwiLink = null as any;
  kaspiLink: string = "";
  payformLink: QiwiLink = null as any;
  smartBillingLink: string = "";
  questionsList: Questionnaire[] = [];
  needCheckQuestionnaire: boolean = true;
  showQuestionnaire: boolean = false;

  @Mutation setneedCheckQuestionnaire(val: boolean) {
    this.needCheckQuestionnaire = val;
  }

  @Mutation setshowQuestionnaire(val: boolean) {
    this.showQuestionnaire = val;
  }

  @Action
  public async sendAnswers(req: SendAnswersDTO) {
    await ContractsService.sendAnswers(req);
  }

  @Mutation
  public async setPaySum(val: number) {
    this.paySum = val;
  }

  @MutationAction
  public async getQiwiLink(req: QiwiPay) {
    const qiwiLink = await ContractsService.getPayLink(req);
    return { qiwiLink };
  }

  @MutationAction
  public async getKaspiLink(req: KaspiPay) {
    const kaspiLink = `https://kaspi.kz/pay/AlmatyEnergoSbytOrg?service_id=7539&11882=${req.bin}&11883=${req.contractnumber}`;
    return { kaspiLink };
  }

  @MutationAction
  public async getPayformLink(req: PayformPay) {
    const payformLink = await ContractsService.getPayformPayLink(req);
    return { payformLink };
  }

  @MutationAction
  public async getSmartBillingLink(req: SmartBillingPay) {
    const smartBillingLink = `https://aes.sbp.kz/#/?lang=${req.lang}&sid=${req.sid}&account=${req.account}&bin=${req.bin}`;
    return { smartBillingLink };
  }

  @MutationAction
  public async getPayDetail(query: PayDetailDTO) {
    const payDetail = await ContractsService.getPayDetail(query);
    const paySum: number = 999;
    return { payDetail, paySum };
  }

  @MutationAction
  public async getCurPeriod(id: string) {
    const curPeriod = await ContractsService.getCurPeriod(id);
    return { curPeriod };
  }

  @Mutation setShowPayDialog(val: boolean) {
    this.showPayDialog = val;
  }

  @Mutation setShowRatingDialog(val: boolean) {
    this.showRatingDialog = val;
  }

  @Mutation setBINForRating(BIN: string) {
    this.BINForRating = BIN;
  }
  @Mutation setEmailForRating(Email: string) {
    this.EmailForRating = Email;
  }

  @Mutation setinfoIsLoading(value: boolean) {
    this.contractInfo = new ContractInfo();
    this.infoIsLoading = value;
    this.contractLimits = [];
  }

  @Mutation
  public setSelectedContract(value: number | null) {
    this.selectedContract = value;
  }

  @Action
  public selectContractAndRoute(value: number | null): boolean {
    this.setSelectedContract(value);
    if (value) {
      router.push({
        name: "contract",
        params: { id: `${value}` },
      });
      return true;
    }
    return false;
  }

  @MutationAction
  public async getContracts() {
    const contracts = await ContractsService.getContracts();
    return { contracts };
  }

  @Mutation
  public setContractStatus(value: ContractStatus) {
    this.contractStatus = value;
  }

  @Action
  public async getContractStatus(id: string) {
    const contractStatus = await ContractsService.getContractStatus(id);
    const contractActionRid = contractStatus.contract_add_action_rid;

    this.setContractStatus(contractStatus);

    if (contractActionRid) {
      DialogsModule.setDebtNoticeModal({
        show: true,
        file: null,
        contract: null,
      });

      const report = await ReportsModule.generateReport({
        action: null,
        path: "debt-notification.prpt",
        type: "pdf",
        filename: "Уведомление о задолженности",
        query: {
          iContractAddActionsRID: contractActionRid,
        },
      });

      if (report) {
        const file = URL.createObjectURL(report);
        DialogsModule.setDebtNoticeModal({
          show: true,
          file,
          contract: contractActionRid,
        });
      } else {
        DialogsModule.unsetDebtNoticeModal();
      }
    }
  }

  @MutationAction
  public async getContractInfo(id: string) {
    const contractInfo = await ContractsService.getContractInfo(id);
    const contractLimits = await ContractsService.getContractLimits(id);
    const infoIsLoading: boolean = false;
    return { contractInfo, infoIsLoading, contractLimits };
  }

  @MutationAction
  public async getQuestionnaire(BIN: string) {
    const questionsList = await ContractsService.getQuestionnaire(BIN);
    return { questionsList };
  }

  get hasDebt(): boolean {
    return this.contractStatus !== null && this.contractStatus?.status !== null;
  }

  @Action
  logout() {
    this.setBINForRating(AuthModule.user ? AuthModule.user.bin : "");
    this.setEmailForRating(
      AuthModule.user &&
        AuthModule.user.org_data &&
        AuthModule.user?.org_data.isEmailVerified &&
        AuthModule.user.email
        ? AuthModule.user.email
        : "",
    );
    const lastRatingDate = localStorage.getItem("lastRatingDate");
    const timePassed =
      Number((new Date().getTime() / 1000).toFixed(0)) - Number(lastRatingDate);
    //if (timePassed >= 86400) {
    //  this.setShowRatingDialog(true);
    //} else {
    AuthService.logout();
    //}
  }
}

export const ContractsModule = getModule(ContractsVuexModule);
